/**
 * PolicyAssignmentsService (Phase 7)
 * Admin-only policy assignment workflow:
 *   - publish & assign a policy to required signatories
 *   - list / sign / waive / scan
 * Route: api/PolicyAssignments
 */
import { BaseService } from '../base/BaseService';
export class PolicyAssignmentsService extends BaseService {
    constructor(http, config) {
        const internalConfig = {
            servicePath: config?.servicePath ?? PolicyAssignmentsService.DEFAULT_SERVICE_PATH,
            basePath: PolicyAssignmentsService.BASE_PATH,
            endpoints: config?.endpoints,
        };
        super(http, internalConfig);
    }
    async publish(request) {
        const response = await this.publishRaw(request);
        if (!response.data)
            throw new Error('Publish failed');
        return response.data;
    }
    publishRaw(request) {
        return this.http.post(this.buildUrl('publish'), request);
    }
    async list(filters) {
        const response = await this.listRaw(filters);
        return response.data ?? [];
    }
    listRaw(filters) {
        const params = {};
        if (filters?.userId)
            params.userId = filters.userId;
        if (filters?.status)
            params.status = filters.status;
        if (filters?.policyId)
            params.policyId = filters.policyId;
        if (filters?.take && filters.take > 0)
            params.take = filters.take;
        return this.http.get(this.buildUrlWithParams(undefined, params));
    }
    async sign(id, request = {}) {
        const response = await this.signRaw(id, request);
        if (!response.data)
            throw new Error(`Sign failed for assignment ${id}`);
        return response.data;
    }
    signRaw(id, request = {}) {
        return this.http.post(this.buildUrl(`${id}/sign`), request);
    }
    async waive(id, request) {
        const response = await this.waiveRaw(id, request);
        if (!response.data)
            throw new Error(`Waive failed for assignment ${id}`);
        return response.data;
    }
    waiveRaw(id, request) {
        return this.http.post(this.buildUrl(`${id}/waive`), request);
    }
    async scan() {
        const response = await this.scanRaw();
        if (!response.data)
            throw new Error('Scan failed');
        return response.data;
    }
    scanRaw() {
        return this.http.post(this.buildUrl('scan'), {});
    }
    /**
     * DELETE api/PolicyAssignments/{id}
     *
     * Hard-deletes a single assignment row. Prefer `waive(...)` for the
     * audit-friendly path; this is the destructive option.
     */
    async deleteAssignment(id) {
        const response = await this.deleteAssignmentRaw(id);
        return response.success;
    }
    deleteAssignmentRaw(id) {
        return this.http.delete(this.buildUrl(`${id}`));
    }
    /**
     * DELETE api/PolicyAssignments/by-policy/{policyId}
     *
     * Hard-deletes every assignment for a policy. Used by the policy-detail
     * danger zone when HR wants to clear the slate before re-publishing.
     * Returns the number of rows removed.
     */
    async deleteAllForPolicy(policyId) {
        const response = await this.deleteAllForPolicyRaw(policyId);
        return response.data?.removed ?? 0;
    }
    deleteAllForPolicyRaw(policyId) {
        return this.http.delete(this.buildUrl(`by-policy/${policyId}`));
    }
}
PolicyAssignmentsService.DEFAULT_SERVICE_PATH = 'hr';
PolicyAssignmentsService.BASE_PATH = 'api/PolicyAssignments';
//# sourceMappingURL=PolicyAssignmentsService.js.map